import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RT, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import { useAppearance } from "../../contexts/AppearanceContext";
import { Button } from "../../components/ui/button";
import { Download, GitBranch, Clock, AlertCircle, ThumbsDown } from "lucide-react";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#ef4444","#f97316","#f59e0b","#10b981","#3b82f6","#8b5cf6"];

function fmtK(v) { if (!v) return "R$ 0";
  if (v >= 1000000) return `R$ ${(v/1000000).toFixed(1)}M`;
  if (v >= 1000) return `R$ ${(v/1000).toFixed(0)}k`;
  return `R$ ${Math.round(v)}`;
}
function fmt(v) { return new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL",maximumFractionDigits:0}).format(v||0); }

export default function PipelineReport({filterHook }) {
  const { t } = useTranslation();
  const [funil, setFunil] = useState(null);
  const [perda, setPerda] = useState(null);
  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("funil");
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3b82f6";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const baseFilters = filterHook.buildApiFilters({ status: ["ganho", "perdido", null] });
      const [funilRes, perdaRes, slaRes] = await Promise.all([
        axios.post(`${API}/reports/negocios`, { filters: { ...baseFilters, status: [] }, group_by: "estagio_id" }, { withCredentials: true }),
        axios.post(`${API}/reports/negocios`, { filters: { ...filterHook.buildApiFilters(), status: ["perdido"] }, group_by: "motivo_perda" }, { withCredentials: true }),
        axios.post(`${API}/reports/sla-violations`, { pipeline_ids: filterHook.filters.pipelineIds.length ? filterHook.filters.pipelineIds : filterHook.pipelines.map(p=>p.id), responsavel_ids: filterHook.filters.responsavelIds }, { withCredentials: true }),
      ]);
      setFunil(funilRes.data);
      setPerda(perdaRes.data);
      setSla(slaRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterHook.filters, filterHook.buildApiFilters, filterHook.pipelines]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    if (sla?.violations) {
      const ws = XLSX.utils.json_to_sheet(sla.violations.map(v => ({
        "Negócio": v.titulo, "Responsável": v.responsavel, "Etapa": v.stage,
        "Tipo": v.tipo, "Dias": v.days, "Limite": v.limit, "MRR": v.mrr,
      })));
      XLSX.utils.book_append_sheet(wb, ws, "SLA Violations");
    }
    if (perda?.groups) {
      const ws2 = XLSX.utils.json_to_sheet(perda.groups.map(g => ({"Motivo": g.label, "Deals": g.count, "MRR Perdido": g.mrr})));
      XLSX.utils.book_append_sheet(wb, ws2, "Motivos de Perda");
    }
    XLSX.writeFile(wb, `relatorio_pipeline_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const TABS = [
    { k: "funil", l: "Funil", icon: GitBranch },
    { k: "sla", l: "SLA Violations", icon: AlertCircle },
    { k: "perda", l: "Análise de Perda", icon: ThumbsDown },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
          {TABS.map(item => {
            const TabIcon = item.icon;
            return (
            <button key={item.k} onClick={() => setActiveTab(item.k)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === item.k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
              style={activeTab === item.k ? { color: tc500 } : undefined}>
              <TabIcon className="w-3.5 h-3.5" />{item.l}
            </button>
            );
          })}
        </div>
        <Button variant="outline" size="sm" onClick={exportExcel} className="gap-2 text-slate-600">
          <Download className="w-4 h-4" />{t("relatorios.exportExcel")}</Button>
      </div>

      {loading && <div className="flex items-center justify-center h-32 text-slate-400 text-sm">{t("common.loading")}</div>}

      {!loading && activeTab === "funil" && funil && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {(funil.groups || []).slice(0, 4).map(g => (
              <div key={g.key} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{backgroundColor:g.color||tc500}} />
                  <p className="text-xs font-semibold text-slate-500 truncate">{g.label}</p>
                </div>
                <p className="text-xl font-bold text-slate-900">{g.count}</p>
                <p className="text-xs text-slate-400 mt-0.5">{fmtK(g.mrr)}/mês</p>
              </div>
            ))}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">{t("relatorios.conversao")}</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={funil.groups || []} layout="vertical" margin={{top:0,right:60,left:80,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                <XAxis type="number" tick={{fontSize:11}} />
                <YAxis type="category" dataKey="label" tick={{fontSize:11}} width={76} />
                <RT formatter={v => [`${v} deals`]} contentStyle={{fontSize:12}} />
                <Bar dataKey="count" radius={[0,4,4,0]}>
                  {(funil.groups||[]).map((g,i) => <Cell key={i} fill={g.color||COLORS[i%COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {!loading && activeTab === "sla" && sla && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-rose-600 mb-1">Total de Violações</p>
              <p className="text-2xl font-bold text-rose-700">{sla.total_violations}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-600 mb-1">MRR em Risco</p>
              <p className="text-2xl font-bold text-amber-700">{fmtK(sla.mrr_em_risco)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-500 mb-1">{t("ui.etapas_afetadas")}</p>
              <p className="text-2xl font-bold text-slate-700">{sla.by_stage?.length || 0}</p>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700">{t("dashboard.slaAlerts")}</h3>
            </div>
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="border-b border-slate-100">
                    {["Negócio",t("ui.responsavel"),"Etapa","Tipo","Dias","Limite","MRR"].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(sla.violations || []).map(v => (
                    <tr key={v.id} className="border-b border-slate-50 hover:bg-rose-50/30">
                      <td className="px-4 py-2.5 font-medium text-slate-800 truncate max-w-[180px]">{v.titulo}</td>
                      <td className="px-4 py-2.5 text-slate-500">{v.responsavel}</td>
                      <td className="px-4 py-2.5 text-slate-600">{v.stage}</td>
                      <td className="px-4 py-2.5"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${v.tipo==="prazo"?"bg-rose-100 text-rose-700":"bg-amber-100 text-amber-700"}`}>{v.tipo==="prazo"?"Prazo":"Inatividade"}</span></td>
                      <td className="px-4 py-2.5 font-semibold text-rose-600">{v.days}d</td>
                      <td className="px-4 py-2.5 text-slate-400">{v.limit}d</td>
                      <td className="px-4 py-2.5 font-medium" style={{color:tc500}}>{fmtK(v.mrr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!sla.violations?.length && <p className="text-center py-8 text-slate-400 text-sm">Nenhuma violação de SLA encontrada.</p>}
            </div>
          </div>
        </div>
      )}

      {!loading && activeTab === "perda" && perda && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <p className="text-xs font-semibold text-slate-500 mb-1">Total Perdido</p>
              <p className="text-2xl font-bold text-slate-900">{perda.total?.count || 0}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <p className="text-xs font-semibold text-slate-500 mb-1">MRR Perdido</p>
              <p className="text-2xl font-bold text-rose-600">{fmtK(perda.total?.mrr)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <p className="text-xs font-semibold text-slate-500 mb-1">TCV Perdido</p>
              <p className="text-2xl font-bold text-slate-900">{fmtK(perda.total?.tcv)}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Por Motivo de Perda</h3>
              {perda.groups?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={perda.groups} cx="50%" cy="50%" outerRadius={65} dataKey="count">
                        {perda.groups.map((_, i) => <Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                      </Pie>
                      <RT contentStyle={{fontSize:12}} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5 mt-2">
                    {perda.groups.slice(0,5).map((g,i) => (
                      <div key={g.key} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:COLORS[i%COLORS.length]}} />
                          <span className="text-slate-600 truncate max-w-[140px]">{g.label}</span>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className="font-semibold text-slate-700 ml-2">{g.count}</span>
                          <span className="text-slate-400 ml-2">{fmtK(g.mrr)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : <p className="text-center py-8 text-slate-400 text-sm">Nenhuma perda registrada.</p>}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">MRR por Motivo</h3>
              {perda.groups?.length > 0 && (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={perda.groups.slice(0,6)} margin={{top:0,right:10,left:10,bottom:20}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{fontSize:9}} angle={-25} textAnchor="end" interval={0} />
                    <YAxis tick={{fontSize:10}} tickFormatter={v => fmtK(v)} />
                    <RT formatter={v => [fmt(v),"MRR"]} contentStyle={{fontSize:12}} />
                    <Bar dataKey="mrr" fill="#ef4444" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
